import { Observer, extend, isNullOrUndefined as isNOU, remove } from '@syncfusion/ej2-base';
import { getScrollableParent } from '@syncfusion/ej2-popups';
import { events } from '../../common/constant';
import * as constants from '../../common/constant';
import { BlockCommand } from '../plugins/block/block-command';
import { BlockFactory, BlockService, EventService, TableService } from '../services/index';
import { StateManager } from '../services/state-manager';
import { NodeSelection } from '../../selection/selection';
import { PopupRenderer } from '../renderer/common/popup-renderer';
import { BlockActionMenuModule, ContextMenuModule, SlashCommandModule } from '../plugins/menus/index';
import { FloatingIcon } from '../actions/floating-icon';
import { decode, encode, findCellById, getBlockContentElement, getBlockModelById, isAlwaysOnPlaceHolderBlk, NodeCutter, sanitizeHelper, setCursorPosition } from '../../common/utils/index';
import { BlockType } from '../../models/enums';
import { clearBreakTags, findClosestParent, isElementEmpty } from '../../common/utils/dom';
import { ListPlugin } from '../plugins/block/list';
import { UndoRedoAction } from '../actions/undo';
import { BlockEditorMethods } from '../actions/methods';
import { FormattingAction } from '../actions/formatting';
import { LinkModule } from '../plugins/inline/link';
import { InlineContentInsertionModule } from '../plugins/inline/inline-content';
import { ClipboardAction } from '../actions/clipboard';
import { MentionAction } from '../actions/mention';
import { DragAndDropAction } from '../actions/drag';
import { InlineToolbarModule } from '../actions/inline-toolbar';
import { EventAction } from '../actions/event';
import { BlockRenderer } from '../plugins/block/block-renderer';
import { TableSelectionManager } from '../plugins/table/selection-manager';
import { SelectionOverlay } from '../renderer/common/selection-overlay';
export class BlockManager {
    constructor() {
        this.defaultKeyConfig = {
            bold: 'ctrl+b',
            italic: 'ctrl+i',
            underline: 'ctrl+u',
            strikethrough: 'ctrl+shift+x',
            inlineCode: 'ctrl+`',
            link: 'ctrl+k',
            print: 'ctrl+p'
        };
        this.observer = new Observer(this);
    }
    updateContext(BlockEditorObj) {
        extend(this, this, BlockEditorObj);
    }
    initialize() {
        /* Populate all block level properties which are not provided on application end */
        const populatedBlocks = BlockFactory.populateBlockProperties(this.blocks);
        this.blocks = populatedBlocks.slice();
        this.observer.notify('updateEditorBlocks', { blocks: this.blocks });
        this.blockCommand = new BlockCommand(this);
        this.stateManager = new StateManager(this);
        this.blockService = new BlockService(this.blocks);
        this.eventService = new EventService(this);
        this.tableService = new TableService(this);
        this.tableSelectionManager = new TableSelectionManager(this);
        this.blockCommand.createDefaultEmptyBlock();
        this.popupRenderer = new PopupRenderer(this);
        this.blockRenderer = new BlockRenderer(this);
        this.listPlugin = new ListPlugin(this);
        this.nodeSelection = new NodeSelection(this.blockContainer);
        this.nodeCutter = new NodeCutter();
        this.selectionOverlay = new SelectionOverlay(this);
        this.lastHighlightedBlockId = '';
        this.eventAction = new EventAction(this);
        this.floatingIconAction = new FloatingIcon(this);
        this.undoRedoAction = new UndoRedoAction(this);
        this.formattingAction = new FormattingAction(this);
        this.clipboardAction = new ClipboardAction(this);
        this.dragAndDropAction = new DragAndDropAction(this);
        this.mentionAction = new MentionAction(this);
        this.editorMethods = new BlockEditorMethods(this);
        this.inlineContentInsertionModule = new InlineContentInsertionModule(this);
        this.linkModule = new LinkModule(this);
        this.inlineToolbarModule = new InlineToolbarModule(this);
        this.blockActionMenuModule = new BlockActionMenuModule(this);
        this.contextMenuModule = new ContextMenuModule(this);
        this.slashCommandModule = new SlashCommandModule(this);
        this.scrollParentElements = getScrollableParent(this.rootEditorElement);
        this.wireEvents();
        this.initializeKeyBindings();
    }
    wireEvents() {
        this.observer.on(events.destroy, this.destroy, this);
    }
    unwireEvents() {
        this.observer.off(events.destroy, this.destroy);
    }
    /**
     * Initializes the key bindings
     *
     * @returns {void}
     */
    initializeKeyBindings() {
        const config = Object.assign({}, this.defaultKeyConfig, this.keyConfig);
        const map = new Map();
        for (const command in config) {
            if (Object.prototype.hasOwnProperty.call(config, command)) {
                const keyCombo = config[`${command}`].toLowerCase().replace(/\s+/g, '');
                map.set(keyCombo, command);
            }
        }
        this.keyCommandMap = map;
    }
    execCommand(options) {
        switch (options.command) {
            case 'AddBlock':
                this.observer.notify(constants.ADDBLOCK, options.state);
                break;
            case 'SplitBlock':
                this.observer.notify(constants.SPLITBLOCK, options.state);
                break;
            case 'DeleteBlock':
                this.observer.notify(constants.DELETEBLOCK, options.state);
                break;
            case 'DeleteAtCursor':
                this.observer.notify(constants.DELETEATCURSOR, options.state);
                break;
            case 'DuplicateBlock':
                this.observer.notify(constants.DUPLICATEBLOCK, options.state);
                break;
            case 'IndentBlock':
                this.observer.notify(constants.INDENTBLOCK, options.state);
                break;
            case 'MoveBlock':
                this.observer.notify(constants.MOVEBLOCK, options.state);
                break;
            case 'FormattingAction':
                this.observer.notify(constants.FORMATTINGACTION, options.state);
                break;
            case 'DeleteNonMergableBlock':
                this.observer.notify(constants.DELETE_NON_MERGABLEBLOCK, options.state);
                break;
        }
    }
    /**
     * Sets focus to a block element
     *
     * @param {HTMLElement} block The block element to focus
     * @returns {void}
     * @hidden
     */
    setFocusToBlock(block) {
        if (block) {
            block.focus();
            this.currentFocusedBlock = block;
        }
    }
    /**
     * Fetches the editor blocks from service
     *
     * @returns {BlockModel[]} The editor blocks data
     * @hidden
     */
    getEditorBlocks() {
        if (!this.blockService) {
            return [];
        }
        return this.blockService.getBlocks();
    }
    /**
     * Populates the editor blocks data with the given blocks
     *
     * @param {BlockModel[]} blocks The blocks to set for the editor
     * @returns {void}
     * @hidden
     */
    setEditorBlocks(blocks) {
        if (!this.blockService) {
            return;
        }
        this.blockService.setBlocks(blocks);
    }
    /**
     * Gets a block element by ID
     *
     * @param {string} blockId The block ID
     * @returns {HTMLElement | null} The block element or null if not found
     * @hidden
     */
    getBlockElementById(blockId) {
        return this.blockContainer.querySelector(`#${blockId}`);
    }
    /**
     * Adjusts the view to focus on the current block
     *
     * @returns {void}
     * @hidden
     */
    adjustViewForFocusedBlock() {
        if (!this.currentFocusedBlock) {
            return;
        }
        const editorBlocks = this.getEditorBlocks();
        const lastBlock = editorBlocks[editorBlocks.length - 1];
        const containerRect = this.rootEditorElement.getBoundingClientRect();
        const blockRect = this.currentFocusedBlock.getBoundingClientRect();
        if (lastBlock && lastBlock.id === this.currentFocusedBlock.id) {
            this.rootEditorElement.scrollTo({ top: this.rootEditorElement.scrollHeight });
        }
        else if (blockRect.bottom > containerRect.bottom) {
            this.rootEditorElement.scrollTop += blockRect.bottom - containerRect.bottom;
        }
    }
    /**
     * Sets the focus and UI for a new block
     *
     * @param {HTMLElement} blockElement The block element to focus
     * @returns {void}
     * @hidden
     */
    setFocusAndUIForNewBlock(blockElement) {
        this.togglePlaceholder(this.currentFocusedBlock, false);
        this.setFocusToBlock(blockElement);
        setCursorPosition(getBlockContentElement(blockElement), 0);
        this.togglePlaceholder(this.currentFocusedBlock, true);
        this.floatingIconAction.showFloatingIcons(this.currentFocusedBlock);
    }
    /**
     * Removes the focus and UI for the given block
     *
     * @param {HTMLElement} blockElement The block element to remove focus
     * @returns {void}
     * @hidden
     */
    removeFocusAndUIForBlock(blockElement) {
        this.togglePlaceholder(blockElement, false);
        this.currentFocusedBlock = null;
        this.floatingIconAction.hideFloatingIcons();
    }
    /**
     * Gets the placeholder value for the given block element.
     *
     * @param {BlockModel} block The block model to get placeholder for.
     * @returns {string} The placeholder value for the given block type.
     * @hidden
     */
    getPlaceholderValue(block) {
        const props = block.properties;
        const tableCell = findCellById(block.parentId, this.getEditorBlocks());
        if (tableCell || (props && props.placeholder && props.placeholder !== '')) {
            return props.placeholder;
        }
        let constant = block.blockType.charAt(0).toLowerCase() + block.blockType.slice(1);
        if (block.blockType.endsWith(BlockType.Heading) && props && props.level) {
            constant += props.level.toString();
        }
        return this.localeJson[constant];
    }
    /**
     * Toggles the placeholder for a block element
     *
     * @param {HTMLElement} blockElement The block element
     * @param {boolean} isFocused Whether the block is currently focused
     * @returns {void}
     * @hidden
     */
    togglePlaceholder(blockElement, isFocused) {
        const blockModel = blockElement ? getBlockModelById(blockElement.id, this.getEditorBlocks()) : null;
        const isTableBlock = blockElement && findClosestParent(blockElement, `.${constants.TABLE_BLOCK_CLS}`);
        const isCalloutBlock = blockElement && findClosestParent(blockElement, `.${constants.CALLOUT_BLOCK_CLS}`);
        const isQuoteBlock = blockElement && findClosestParent(blockElement, `.${constants.QUOTE_BLOCK_CLS}`);
        const isCalloutHasOneChild = isCalloutBlock && isCalloutBlock.querySelectorAll(`.${constants.BLOCK_CLS}`).length === 1;
        const isQuoteHasOneChild = isQuoteBlock && isQuoteBlock.querySelectorAll(`.${constants.BLOCK_CLS}`).length === 1;
        const isAlwaysOnBlock = blockModel && isAlwaysOnPlaceHolderBlk(blockModel.blockType);
        const tableCriteria = (isTableBlock &&
            ((blockModel && !(blockModel.properties.placeholder)) || !isFocused));
        const isNullPlaceholder = (blockModel && (blockModel && !('placeholder' in blockModel.properties)));
        if (!blockModel || isNullPlaceholder || tableCriteria || (isAlwaysOnBlock && !isFocused) || (isCalloutHasOneChild && !isFocused) ||
            (isQuoteHasOneChild && !isFocused)) {
            return;
        }
        const blockType = blockElement.getAttribute('data-block-type');
        const placeholderValue = this.getPlaceholderValue(blockModel);
        const contentEle = getBlockContentElement(blockElement);
        const isEmptyContent = isElementEmpty(contentEle);
        contentEle.setAttribute('placeholder', isEmptyContent && isFocused ? placeholderValue : '');
        if (isEmptyContent && blockType !== BlockType.Code) {
            clearBreakTags(contentEle);
        }
    }
    /**
     * Removes all placeholder attributes from block contents
     * and refreshes the placeholder for the current focused block
     *
     * @returns {void}
     * @hidden
     */
    refreshPlaceholder() {
        this.rootEditorElement.querySelectorAll('.' + constants.CONTENT_CLS).forEach((el) => {
            const blockEle = el.closest(`.${constants.BLOCK_CLS}`);
            this.togglePlaceholder(blockEle, false);
        });
        if (this.currentFocusedBlock) {
            this.togglePlaceholder(this.currentFocusedBlock, true);
        }
    }
    /**
     * Sets the cursor position after adding a bulk block (Clipboard paste)
     *
     * @param {string} insertionType - The type of insertion (blocks or text)
     * @returns {void}
     * @hidden
     */
    setCursorAfterBulkBlockAddition(insertionType) {
        let cursorElement = this.currentFocusedBlock;
        let cursorpos = cursorElement.textContent.length;
        if (insertionType === 'blocks') {
            cursorElement = (cursorElement.nextElementSibling || cursorElement);
            cursorpos = 0;
            this.togglePlaceholder(this.currentFocusedBlock, false);
            this.togglePlaceholder(cursorElement, true);
        }
        this.setFocusToBlock(cursorElement);
        setCursorPosition(getBlockContentElement(cursorElement), cursorpos);
    }
    /**
     * Updates focus and cursor position
     *
     * @param {HTMLElement} blockElement The block element to split
     * @returns {void}
     * @hidden
     */
    updateFocusAndCursor(blockElement) {
        if (blockElement) {
            const content = getBlockContentElement(blockElement);
            this.setFocusToBlock(blockElement);
            setCursorPosition(content, 0);
            this.floatingIconAction.showFloatingIcons(blockElement);
        }
    }
    /**
     * Serializes the given value for HTML encoding and sanitization
     *
     * @param {string} value The value to serialize
     * @returns {string} The serialized value
     * @hidden
     */
    serializeValue(value) {
        if (!isNOU(value) && value.trim() !== '') {
            if (this.enableHtmlEncode) {
                value = sanitizeHelper(decode(value), this.enableHtmlSanitizer);
                value = encode(value);
            }
            else {
                value = sanitizeHelper(value, this.enableHtmlSanitizer);
            }
        }
        return value;
    }
    removeAndNullify(element) {
        if (element) {
            if (!isNOU(element.parentNode)) {
                remove(element);
            }
            else {
                element.innerHTML = '';
            }
        }
    }
    destroyBlockEditor() {
        const properties = [
            'currentHoveredBlock',
            'currentFocusedBlock',
            'blockContainer'
        ];
        for (const prop of properties) {
            const element = prop;
            this.removeAndNullify(this[element]);
            this[element] = null;
        }
    }
    destroy() {
        this.scrollParentElements = [];
        this.unwireEvents();
        if (!isNOU(this.updateTimer)) {
            clearInterval(this.updateTimer);
            this.updateTimer = null;
        }
        this.popupRenderer = null;
        if (this.selectionOverlay) {
            this.selectionOverlay.destroy();
        }
        this.selectionOverlay = null;
        this.inlineToolbarModule = null;
        this.inlineContentInsertionModule = null;
        this.slashCommandModule = null;
        this.contextMenuModule = null;
        this.blockActionMenuModule = null;
        this.linkModule = null;
        this.nodeSelection = null;
        this.nodeCutter = null;
        this.popupRenderer = null;
        this.lastHighlightedBlockId = null;
        this.eventAction = null;
        this.formattingAction = null;
        this.listPlugin = null;
        this.editorMethods = null;
        this.mentionAction = null;
        this.stateManager = null;
        this.blockService = null;
        this.eventService = null;
        this.blockCommand = null;
        this.blockRenderer = null;
        this.tableService = null;
        this.tableSelectionManager = null;
        this.keyCommandMap = null;
        this.defaultKeyConfig = null;
        this.dragAndDropAction = null;
        this.undoRedoAction = null;
        this.updateTimer = null;
        this.destroyBlockEditor();
    }
}
